import React, { useState, useEffect, useMemo } from 'react';
import { Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { updateStatement, uploadFoldersFile } from '../../../../slices/thunks';
import { createSelector } from 'reselect';
import { setRequireCloseModal } from '../../../../slices/statement/reducer';
import useFolderPaths from '../../../../Components/Hooks/useFolderPathsHooks';
import useFileType from '../../../../Components/Hooks/useFileTypeHooks';
import { DatePickerInput, DatesProvider } from '@mantine/dates';

const ChargeStatementDocumentForm = ({ data, isEdit, handleToggleModal, isOpen, activeTab, toggleTab }) => {
	const dispatch = useDispatch();
	const [isDisabled, setIsDisabled] = useState(false);
	const [folderPaths, setFolderPaths] = useState([]);

	const selectorStatements = createSelector(
		state => state.Statements,
		statement => ({
			isUpdating: statement.isUpdating,
			isUpdated: statement.isUpdated,
			isUpdateError: statement.isUpdateError,
			requireCloseModal: statement.requireCloseModal
		})
	);

	const { isUpdating, requireCloseModal } = useSelector(selectorStatements);

	const folderPathsRequest = useMemo(
		() => ({
			ids: data?.guide_ids ?? [],
			middle_path: 'Medio Ambiente/Residuo',
		}),
		[data?.guide_ids]
	);

	const { folderPaths: initialFolderPaths, isLoaded } = useFolderPaths(
		folderPathsRequest,
		Boolean(isOpen && folderPathsRequest.ids?.length)
	);
	const { file_type } = useFileType(isOpen ? 'CD' : null);

	useEffect(() => {
		if (isLoaded && Array.isArray(initialFolderPaths)) {
			setFolderPaths(initialFolderPaths);
		}
	}, [isLoaded, initialFolderPaths]);

	const uploadDocument = async (file, paths, fileTypeId, establishmentId) => {
		const validPaths = (paths || []).filter(
			path => typeof path === 'string' && path.trim().length > 0
		);
		if (!validPaths.length) {
			throw new Error(
				'No hay rutas de carpeta válidas para las guías de la declaración.'
			);
		}
		if (!fileTypeId) {
			throw new Error('No se encontró el tipo de archivo CD.');
		}
		if (!establishmentId) {
			throw new Error('La declaración debe tener un establecimiento asociado.');
		}

		const formData = new FormData();
		formData.append('file', file);
		formData.append('file_type_id', fileTypeId);
		formData.append('establishment_id', establishmentId);
		validPaths.forEach(path => formData.append('folder_paths', path));

		try {
			const response = await dispatch(uploadFoldersFile(formData)).unwrap();
			if (response) {
				return response;
			} else {
				throw new Error('File upload failed');
			}
		} catch (error) {
			console.error('Error uploading file:', error);
			throw error;
		}
	};

	const validation = useFormik({
		initialValues: {
			date_document_statement: new Date(),
			document_statement: '',
		},
		validationSchema: Yup.object({
			date_document_statement: Yup.date().required('Campo requerido'),
			document_statement: Yup.mixed()
				.required('El documento Certificado Declaración es requerido')
				.test('documentFormat', 'Solo puede subir extensiones .pdf', value => {
					if (value) {
						const supportedFormats = ['pdf'];
						return supportedFormats.includes(value.name.split('.').pop());
					}
					return true;
				})
				.test('documentSize', 'El archivo no puede superar el tamaño máximo de 10MB', value => {
					if (value) {
						return value.size <= 10485760;
					}
					return true;
				})
		}),
		onSubmit: async (values) => {
			setIsDisabled(true);

			let documents = null;
			if (values.document_statement && isLoaded) {
				const monthsInSpanish = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
				let year = values.date_document_statement.getFullYear();
				let month = monthsInSpanish[values.date_document_statement.getMonth()];
				let result = `/${year}/${month}`;
				const folder = `/Medio Ambiente/Residuo/Declaraciones` + result;
				const updatedFolderPaths = [...folderPaths, folder];
				setFolderPaths(updatedFolderPaths);

				console.log('data before send upload document: ', data)

				try {
					documents = await uploadDocument(
						values.document_statement,
						updatedFolderPaths,
						file_type?._id,
						data.establishment_id
					);
				} catch (error) {
					toast.error(
						error?.message || 'Error al subir el certificado de declaración.',
						{ autoClose: 5000 }
					);
					setIsDisabled(false);
					return;
				}
				
				const document = documents[documents.length - 1];
				const updatedStatement = {
					...data,
					date_document_statement: values.date_document_statement,
					document_statement_id: document._id,
				};

				await dispatch(updateStatement(updatedStatement));
			}
			setIsDisabled(false);
		},
	});

	const handleFileChange = (event) => {
		const file = event.currentTarget.files[0];
		validation.setFieldValue("document_statement", file);
	};

	useEffect(() => {
		if (!isUpdating) {
			setIsDisabled(false);
		}
	}, [isUpdating]);

	useEffect(() => {
		if (isOpen) {
			if (requireCloseModal) {
				dispatch(setRequireCloseModal(false));
				handleToggleModal();
			}
		}
	}, [requireCloseModal]);

	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length != 0) { toast.error('Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.', { autoClose: 4000 }); }
				validation.handleSubmit();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				<TabContent activeTab={'1'}>
					<TabPane tabId='1'>
						<Row className='g-3'>
							<Col sm={3}>
								<DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
									<DatePickerInput
										label='Fecha'
										id='date_document_statement'
										name='date_document_statement'
										placeholder="Seleccione una fecha"
										value={validation.values.date_document_statement}
										onChange={(date) => {
											validation.setFieldValue("date_document_statement", date || '');
										}}
										onBlur={validation.handleBlur}
										valueFormat='DD-MM-YYYY'
										maxDate={new Date()}
										error={
											validation.touched['date_document_statement'] &&
												validation.errors['date_document_statement']
												? validation.errors['date_document_statement'] : ''
										}
										clearable={true}
									/>
								</DatesProvider>
							</Col>
							<Col lg={9}>
								<Label for="guide_document">
									Certificado de declaración
								</Label>
								<Input
									id="guide_document"
									name="guide_document"
									type="file"
									onChange={handleFileChange}
								/>
								{validation.errors.document_statement && (
									<div className="invalid-feedback d-block">
										{validation.errors.document_statement}
									</div>
								)}
							</Col>
						</Row>
					</TabPane>
				</TabContent>
			</ModalBody>
			<div className='modal-footer'>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModal();
						}} className='btn-danger btn-label'>
						<i className="icon-x label-icon"></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						id='add-btn'
						disabled={isDisabled}
					>
						{isDisabled ? (
							<i className='label-icon'><Spinner size='sm' /></i>
						) : <i className={`${Icons.check2} label-icon`}></i>}
						Guardar
					</button>
				</div>
			</div>
		</Form>
	);
};

export default ChargeStatementDocumentForm;
